import { http } from './http';
import axios from 'axios';
import { webAPIUrl } from './AppSettings';

// #region Category
export interface CategoryData {
    categoryId: number;
    name: string;
    icon: string;
    description: string;
    questionCount: number;
}

export interface CategoryPostRequest {
    name: string;
    icon: string;
    description: string;
}

export const getCategories = async (): Promise<CategoryData[]> => {
    const result = await http<CategoryData[]>({
        path: '/categories',
    });
    if (result.ok && result.body) {
        return result.body;
    } else {
        return [];
    }
};

export const getCategoriesWithCounters = async (): Promise<CategoryData[]> => {
    const result = await http<CategoryData[]>({
        path: '/categories?counters=1',
    });
    if (result.ok && result.body) {
        return result.body;
    } else {
        return [];
    }
};

export const getCategory = async (categoryId: number): Promise<CategoryData | null> => {
    const result = await http<CategoryData>({
        path: `/categories/${categoryId}`,
    });
    if (result.ok && result.body) {
        return result.body;
    } else {
        return null;
    }
};

export const postCategory = async (category: CategoryPostRequest): Promise<CategoryData | undefined> => {
    const result = await http<CategoryData, CategoryPostRequest>({
        path: `/categories`,
        method: 'POST',
        body: category,
    });
    if (result.ok && result.body) {
        return result.body;
    } else {
        return undefined;
    }
};

export const putCategory = async (
    categoryId: number,
    category: CategoryPostRequest
): Promise<CategoryData | undefined> => {
    const result = await http<CategoryData, CategoryPostRequest>({
        path: `/categories/${categoryId}`,
        method: 'PUT',
        body: category,
    });
    if (result.ok && result.body) {
        return result.body;
    } else {
        return undefined;
    }
};

export const deleteCategory = async (categoryId: number): Promise<boolean> => {
    const result = await http<any>({
        path: `/categories/${categoryId}`,
        method: 'DELETE',
    });
    return result.ok;
};

// #endregion

//#region Question

export interface QuestionPostRequest {
    categoryId: number;
    title: string;
    content: string;
    labels: string;
    isPromoted: boolean;
    isPublic: boolean;
    isPublished: boolean;
    isArchived: boolean;
    userId: number;
}

export interface QuestionDataResponse {
    questionId: number;
    categoryId: number;
    categoryName: string;
    title: string;
    labels: string;
    updated: string;
    content: string;
    isPublic: boolean;
    isPublished: boolean;
    isArchived: boolean;
    isPromoted: boolean;
}

export interface QuestionData {
    questionId: number;
    categoryId: number;
    categoryName: string;
    title: string;
    labels: string;
    updated: Date;
    content: string;
    isPublic: boolean;
    isPublished: boolean;
    isArchived: boolean;
    isPromoted: boolean;
}

const mapQuestionData = (question: QuestionDataResponse): QuestionData => ({
    ...question,
    updated: new Date(question.updated),
});

export const getRecentQuestions = async (): Promise<QuestionData[]> => {
    const result = await http<QuestionDataResponse[]>({
        path: '/questions/recent',
    });
    if (result.ok && result.body) {
        return result.body.map(mapQuestionData);
    } else {
        return [];
    }
};

export const getCategoryQuestions = async (categoryId: number): Promise<QuestionData[]> => {
    const result = await http<QuestionDataResponse[]>({
        path: `/questions?category=${categoryId}`,
    });
    if (result.ok && result.body) {
        return result.body.map(mapQuestionData);
    } else {
        return [];
    }
};

export const getQuestion = async (questionId: number): Promise<QuestionData | null> => {
    const result = await http<QuestionDataResponse>({
        path: `/questions/${questionId}`,
    });
    if (result.ok && result.body) {
        return mapQuestionData(result.body);
    } else {
        return null;
    }
};

export const getQuestionForEdit = async (questionId: number): Promise<QuestionData | null> => {
    const result = await http<QuestionDataResponse>({
        path: `/questions/${questionId}/edit`,
    });
    if (result.ok && result.body) {
        return mapQuestionData(result.body);
    } else {
        return null;
    }
};

export const searchQuestions = async (query: string): Promise<QuestionData[]> => {
    const result = await http<QuestionDataResponse[]>({
        path: `/questions?search=${query}`,
    });
    if (result.ok && result.body) {
        return result.body.map(mapQuestionData);
    } else {
        return [];
    }
};

export const getQuestionsWithFilter = async ({ published = null, archived = null, search = null } = {}): Promise<
    QuestionData[]
> => {
    const result = await http<QuestionDataResponse[]>({
        path: `/questions/filter?published=${published != null ? (published ? 1 : 0) : ''}&archived=${
            archived != null ? (archived ? 1 : 0) : ''
        }&search=${search != null ? search : ''}`,
    });
    if (result.ok && result.body) {
        return result.body.map(mapQuestionData);
    } else {
        return [];
    }
};

export const postQuestion = async (question: QuestionPostRequest): Promise<QuestionData | undefined> => {
    const result = await http<QuestionDataResponse, QuestionPostRequest>({
        path: `/questions`,
        method: 'POST',
        body: question,
    });
    if (result.ok && result.body) {
        return mapQuestionData(result.body);
    } else {
        return undefined;
    }
};

export const putQuestion = async (
    questionId: number,
    question: QuestionPostRequest
): Promise<QuestionData | undefined> => {
    const result = await http<QuestionDataResponse, QuestionPostRequest>({
        path: `/questions/${questionId}`,
        method: 'PUT',
        body: question,
    });
    if (result.ok && result.body) {
        return mapQuestionData(result.body);
    } else {
        return undefined;
    }
};

export const deleteQuestion = async (questionId: number): Promise<boolean> => {
    const result = await http<any>({
        path: `/questions/${questionId}`,
        method: 'DELETE',
    });
    return result.ok;
};

//#endregion

//#region Login

export interface LoginRequest {
    email: string;
    password: string;
}
export interface LoginResponse {
    token: string;
}

export const login = async (credentials: LoginRequest): Promise<boolean> => {
    const result = await http<LoginResponse, LoginRequest>({
        path: '/accounts/login',
        method: 'POST',
        body: credentials,
    });
    if (result.ok) {
        const { token } = result.body;
        localStorage.setItem('jwt', token);
        return true;
    } else {
        return false;
    }
};

//#endregion

//#region Attachment

export interface AttachmentData {
    attachmentId: number;
    fileName: string;
}

export interface AttachmentList {
    attachments: string;
}

export const getAttachments = async (questionId: number): Promise<AttachmentData[]> => {
    const result = await http<AttachmentData[]>({
        path: `/attachments?question=${questionId}`,
    });
    if (result.ok && result.body) {
        return result.body;
    } else {
        return [];
    }
};

export const addAttachments = async (questionId: number, attachments: AttachmentList): Promise<boolean> => {
    const result = await http<any, AttachmentList>({
        path: `/attachments/${questionId}/add`,
        method: 'POST',
        body: attachments,
    });
    return result.ok;
};

export const removeAttachments = async (attachments: AttachmentList): Promise<boolean> => {
    const result = await http<any, AttachmentList>({
        path: `/attachments`,
        method: 'POST',
        body: attachments,
    });
    return result.ok;
};

export const uploadAttachment = async (file: File): Promise<AttachmentData | undefined> => {
    const formData = new FormData();
    formData.append('File', file);

    let responseData: AttachmentData = undefined;
    await axios
        .post(`${webAPIUrl}/attachments`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progress) => {
                // let completed = Math.round((progress.loaded * 100) / progress.total);
            },
        })
        .then((response) => {
            responseData = response.data;
        })
        .catch((error) => {
            console.log(error);
        });

    return responseData;
};

//#endregion
